import React from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { Card, JalaliDatePicker, PageHeader, KpiCard, Skeleton as SkeletonBlock, Button } from '@parsina/framework';
import { useOptionalToast } from '@parsina/framework/clinical/src/lib/useOptionalToast';
import { ReceptionDaySheetSkeleton } from './ReceptionSkeleton';
import { statusColors, CT, soft } from '@parsina/framework/tokens';
import { Building2, CalendarDays, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import ConfirmDialog from '@parsina/framework/clinical/src/components/common/ConfirmDialog';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import AppointmentRow from '../../components/AppointmentRow';
import NextPatientHero from '../../components/NextPatientHero';
import ForgottenVisitBanner from '../../components/ForgottenVisitBanner';
import NoAppointments from './NoAppointments';
import BookAppointmentDialog from '../../components/BookAppointmentDialog';
import RescheduleAppointmentDialog from '../../components/RescheduleAppointmentDialog';
import { useReceptionBooking } from '../../hooks/useReceptionBooking';
import NoClinicGuide from '../../components/NoClinicGuide.jsx';
import ClinicContextChip from '../../components/ClinicContextChip.jsx';
import {
  useReception, useWeekAhead, useTodayIncome,
  forgottenVisits,
} from '../../hooks/useReception';
import { buildDayModel, dayCounts, shiftISO, makeDateLabel, resolvePlace, stripDayOfMonth } from './Reception.shared';
import { usePresenceSchedule } from '../../hooks/usePresenceSchedule';
import { useTranslation, MIRROR_ICON } from '@parsina/framework/clinical/src/i18n/index';
import { useDoctor } from '@parsina/framework/clinical/src/hooks/useDoctor';
import { ALL_CLINICS, useActiveClinic, scopeToClinic } from '../../hooks/useActiveClinic';
import { useWalletBalance } from '@parsina/framework/clinical/src/hooks/useWalletBalance';
import { IRAN_OFFSET_MS } from '@parsina/framework/clinical/src/lib/jalali';
import { useCreditPurchase } from '../../hooks/useCreditPurchase';
import { incomeGroups, displayCurrencyFor } from '../../lib/clinicMoney';

// A token colour at low alpha — keeps tinted surfaces on the token, no raw hex.

// Thin alias over the framework skeleton atom (keeps the compact w/h call sites) —
// the rail cards' per-value placeholders. The day sheet has its own skeleton.
const Skeleton = ({ w = 64, h = 36 }) => <SkeletonBlock width={w} height={h} />;

// ── Block (one section of the day) ────────────────────────────────────────────
const Block = ({ title, count, countLabel, tint, tintText, collapsible, defaultOpen = true, children }) => {
  const { formatDigits } = useTranslation();
  const [open, setOpen] = React.useState(defaultOpen);
  const shown = collapsible ? open : true;

  const head = (
    <>
      <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: C.textPrimary }}>{title}</h3>
      <span style={{
        fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20,
        background: tint, color: tintText,
      }}>
        {formatDigits(count)} {countLabel}
      </span>
      {collapsible && (
        <span style={{ marginInlineStart: 'auto', color: C.textMuted, fontSize: 12 }}>{shown ? '▲' : '▼'}</span>
      )}
    </>
  );

  const headStyle = {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
    padding: '14px 20px', borderBottom: shown ? `1px solid ${C.border}` : 'none',
    background: 'transparent', border: 'none', textAlign: 'start',
    cursor: collapsible ? 'pointer' : 'default',
  };

  return (
    <Card style={{ padding: 0, overflow: 'hidden', border: 'none', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      {collapsible
        ? <button onClick={() => setOpen(o => !o)} style={headStyle}>{head}</button>
        : <div style={headStyle}>{head}</div>}
      {shown && <div style={{ padding: '14px 16px 8px' }}>{children}</div>}
    </Card>
  );
};

// ── Rail cards ────────────────────────────────────────────────────────────────

const RailCard = ({ title, warn, children }) => (
  <Card style={{
    padding: '16px 18px',
    border: warn ? `1.5px solid ${soft(C.amber, 0.45)}` : 'none',
    background: warn ? soft(C.amber, 0.08) : 'white',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  }}>
    <div style={{ fontSize: 11.5, fontWeight: 700, color: C.textMuted, marginBottom: 10 }}>{title}</div>
    {children}
  </Card>
);

// Today's income: the doctor's share of today's invoiced work, in its own
// currency's display unit (toman for IRR) — minor units stay on the wire.
//
// The day spans every clinic of the doctor's, so it can hold two currencies: then
// each currency gets its own line and its own «unsettled» pill (carrying its unit,
// since two bare figures side by side would not say which is which), and nothing
// adds them. A day with no line states its zero in `currency`, the clinic's.
export const IncomeCard = ({ income, currency, onOpen }) => {
  const { t, formatMoney, moneyUnit } = useTranslation();
  const groups = income ? incomeGroups(income, currency) : [];
  const mixed = groups.length > 1;
  return (
  <RailCard title={t('dashboard.rail.income')}>
    {income === null && <Skeleton w={110} h={26} />}
    {income === false && (
      <div style={{ fontSize: 12, color: C.textMuted }}>{t('common.state.unavailable')}</div>
    )}
    {income && (
      <>
        {groups.map((g) => (
          <div key={g.currency} style={{ fontSize: 20, fontWeight: 800, color: C.textPrimary, lineHeight: 1.2 }}>
            {formatMoney(g.shareRial, { currency: g.currency })} <span style={{ fontSize: 12, fontWeight: 600, color: C.textMuted }}>{moneyUnit(g.currency)}</span>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 20, padding: '2px 9px', background: statusColors.arrived.bg, color: statusColors.arrived.text }}>
            {t('dashboard.rail.visitCount', { count: income.visitCount })}
          </span>
          {groups.map((g) => g.unsettledRial > 0 && (
            <span key={g.currency} style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 20, padding: '2px 9px', background: C.amberLight, color: CT.warnInk }}>
              {t('dashboard.rail.unsettled', {
                amount: mixed
                  ? `${formatMoney(g.unsettledRial, { currency: g.currency })} ${moneyUnit(g.currency, 'short')}`
                  : formatMoney(g.unsettledRial, { currency: g.currency }),
              })}
            </span>
          ))}
        </div>
        <button onClick={onOpen} style={{
          marginTop: 12, height: 30, padding: '0 12px', width: '100%',
          background: CT.surface2, color: C.navy, border: 'none', borderRadius: 8,
          fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
        }}>
          {t('dashboard.rail.financialReport')}
        </button>
      </>
    )}
  </RailCard>
  );
};

// Parsina Credits — the same 50/200 tiers the Credits screen uses. AI features stop
// working when this hits zero; پذیرش's job is to say so before that happens.
const CreditsCard = ({ balance, onOpen }) => {
  const { canBuy } = useCreditPurchase();
  const { t, formatNumber } = useTranslation();
  const low = balance != null && balance < 50;
  const mid = balance != null && !low && balance < 200;
  return (
    <RailCard title={t('dashboard.rail.credit')} warn={low || mid}>
      {balance == null ? <Skeleton w={90} h={26} /> : (
        <>
          <div style={{
            fontSize: 20, fontWeight: 800, lineHeight: 1.2,
            color: low ? CT.dangerInk : mid ? CT.warnInk : C.textPrimary,
          }}>
            {formatNumber(Math.round(balance))} <span style={{ fontSize: 12, fontWeight: 600, color: C.textMuted }}>{t('dashboard.rail.creditUnit')}</span>
          </div>
          {(low || mid) && (
            <div style={{ fontSize: 11.5, color: low ? CT.dangerInk : CT.warnInk, marginTop: 8, lineHeight: 1.6 }}>
              {low ? t('dashboard.rail.creditLow') : t('dashboard.rail.creditMid')}
            </div>
          )}
          {/* «افزایش اعتبار» buys credit: offered only where it is sold and this
              panel has a rail for its price (hooks/useCreditPurchase.js). The
              balance stays. */}
          {canBuy && (
          <button onClick={onOpen} style={{
            marginTop: 12, height: 30, padding: '0 12px', width: '100%',
            background: low ? C.red : CT.surface2,
            color: low ? 'white' : C.navy,
            border: 'none', borderRadius: 8,
            fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
          }}>
            {t('dashboard.rail.topUp')}
          </button>
          )}
        </>
      )}
    </RailCard>
  );
};

// The week ahead as seven tappable day chips — the count answers "how heavy is
// شنبه", the tap replaces arrow-stepping one day at a time.
const WeekStrip = ({ days, selISO, onPick }) => {
  const { t, formatDigits, formatWeekday } = useTranslation();
  return (
  <RailCard title={t('dashboard.rail.weekAhead')}>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
      {days.map(({ iso, count }) => {
        // Jalali in every language, Gregorian for a brand on that calendar.
        const day = stripDayOfMonth(iso);
        // `narrow`, not `short`: seven chips share one 280px rail, so this has to
        // be the one-letter form the strip was built around («ش ی د س چ پ ج»).
        // Persian's `short` is the FULL word («چهارشنبه») and would overflow.
        const wd = formatWeekday(Date.parse(`${iso}T12:00:00Z`), 'narrow');
        const active = iso === selISO;
        return (
          <button key={iso} onClick={() => onPick(iso)} style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
            background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
            flex: 1,
          }}>
            <span style={{ fontSize: 10.5, color: active ? C.navy : C.textMuted, fontWeight: active ? 800 : 600 }}>
              {wd}{day ? ` ${formatDigits(day)}` : ''}
            </span>
            <span style={{
              fontSize: 12, fontWeight: 800, borderRadius: 8, padding: '3px 0', width: '100%', textAlign: 'center',
              background: active ? C.navy : count ? soft(C.navy, 0.06) : CT.surface2,
              color: active ? 'white' : count ? C.navy : C.textMuted,
            }}>
              {count == null ? '·' : count === 0 ? '—' : formatDigits(count)}
            </span>
          </button>
        );
      })}
    </div>
  </RailCard>
  );
};

// ── NavButton (header arrow) ──────────────────────────────────────────────────
const NavBtn = ({ onClick, children }) => (
  <button onClick={onClick} style={{
    width: 32, height: 32, borderRadius: 8, background: CT.surface2,
    border: `1px solid ${C.border}`, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: C.textSecondary,
    transition: 'background 0.12s',
  }}
    onMouseEnter={e => e.currentTarget.style.background = CT.border2}
    onMouseLeave={e => e.currentTarget.style.background = CT.surface2}
  >
    {children}
  </button>
);

// ── Main پذیرش ────────────────────────────────────────────────────────────────
const ReceptionWeb = ({ navigate }) => {
  // Arriving from «پایان ویزیت»: the closure that owned the screen has gone and
  // took the focused element with it, so the page takes focus itself rather
  // than leaving it on <body>. Read once — a later re-render must not steal
  // focus back from whatever the doctor has since clicked.
  const arrivedFromClosure = React.useRef(
    useLocation().state?.from === 'visit-closure',
  ).current;
  const { t, formatDate, formatNumber, formatDigits } = useTranslation();
  const [tick, setTick] = React.useState(Date.now());

  // 30s: the wait/visit clocks are minute-grained; a stale minute reads as a bug.
  React.useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // The P2 rail collapses under the main column below 1180px (DESIGN-CONCEPT §2.2).
  const [narrow, setNarrow] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 1180px)');
    const sync = () => setNarrow(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Iran time (today)
  const now       = new Date(tick + IRAN_OFFSET_MS);
  const nowMin    = now.getUTCHours() * 60 + now.getUTCMinutes();
  const todayISO  = now.toISOString().split('T')[0];

  // Selected day — an absolute ISO date (Iran-local), driven by the date picker.
  // Lives in the URL (?date=YYYY-MM-DD) so refresh/back/shared links reopen the
  // same day; today is the default and stays out of the URL. `replace` keeps
  // day-stepping from stacking history entries.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawDate = searchParams.get('date');
  const selISO  = /^\d{4}-\d{2}-\d{2}$/.test(rawDate || '') ? rawDate : todayISO;
  const setSelISO = (v) => {
    const next = new URLSearchParams(searchParams);
    if (!v || v === todayISO) next.delete('date'); else next.set('date', v);
    setSearchParams(next, { replace: true });
  };
  const isToday   = selISO === todayISO;

  const { appointments: allAppointments, openVisits, loading, error, retry, markArrived, endVisit, withdrawRequest, reload } = useReception(selISO);
  const toast = useOptionalToast();
  // «نوبت جدید» — by the clinic's grant; «لغو درخواست» for the doctor's own pending ask.
  const booking = useReceptionBooking({ selISO, reload, withdrawRequest, toast });
  // «ثبت حضور» is a row action: pending on its own button, failure as a toast
  // (DC §7 routing), success is the row moving into the queue.
  const [arrivingIds, setArrivingIds] = React.useState(() => new Set());
  const arrive = async (id) => {
    if (arrivingIds.has(id)) return;
    setArrivingIds(prev => new Set(prev).add(id));
    try { await markArrived(id); }
    catch { toast.error(t('errors.save.markArrived')); }
    finally { setArrivingIds(prev => { const n = new Set(prev); n.delete(id); return n; }); }
  };
  const { doctor } = useDoctor();

  // Which clinic's desk this day sheet belongs to. The scope is a LENS, not an
  // access rule — everything here was already this doctor's to see; the chip
  // only decides which of it is on screen (see useActiveClinic for why no
  // `x-active-clinic-id` header is sent). Same behaviour as the phone: every
  // clinic by default, unless the doctor picked one — or «خودکار», the presence
  // schedule for the day on screen — today. The two surfaces share the hook so
  // they cannot claim different clinics.
  const presence = usePresenceSchedule();
  const scheduledFor = React.useCallback(
    (clinics) => resolvePlace({ ...presence, clinics, dayISO: selISO, isToday, nowMin }).scheduledClinicId,
    [presence, selISO, isToday, nowMin],
  );
  const clinic = useActiveClinic({ scheduledFor, todayISO });
  const { rows: appointments, hidden: hiddenByClinic } = React.useMemo(
    () => scopeToClinic(allAppointments, clinic.filterId),
    [allAppointments, clinic.filterId],
  );

  // The tag a row carries in «همهٔ مطب‌ها» — and nothing at all otherwise, so a
  // single-clinic day sheet is not decorated with the same name on every line.
  const clinicNameOf = React.useCallback(
    (apt) => (clinic.isAll && clinic.clinics.length > 1
      ? (clinic.clinics.find(c => c.id === apt.clinicId)?.name || '')
      : ''),
    [clinic.isAll, clinic.clinics],
  );

  // Rows per clinic for the picker — the choice reads better as "3 نوبت امروز"
  // than as a bare name, and it is free: the day is already in hand.
  const clinicCounts = React.useMemo(() => {
    const m = new Map();
    (allAppointments || []).forEach(a => {
      if (a.clinicId) m.set(a.clinicId, (m.get(a.clinicId) || 0) + 1);
    });
    return m;
  }, [allAppointments]);
  const walletBalance = useWalletBalance(doctor?.id);
  const income = useTodayIncome(todayISO);
  const weekDays = useWeekAhead(todayISO);

  // ms for the selected Iran-local day (noon avoids DST/rounding edge cases)
  const selIranMs = Date.parse(`${selISO}T12:00:00Z`);

  // Header date label
  const dateLabel = makeDateLabel(selIranMs, { formatDate, withYear: true });

  // One shared day-model with the mobile surface — same queue order, same next
  // patient, same counts (Reception.shared.js).
  const { dayApts, queue, upcoming, done, queuePosOf, nextApt } =
    buildDayModel({ appointments, isToday, nowMin });

  const upcomingList = upcoming.filter(a => !nextApt || a.id !== nextApt.id);

  // Visits presumed forgotten (open > 2h, or from a previous day) — but never the
  // one the hero is already showing as active: one region is loud about it, not two.
  const forgotten = forgottenVisits(openVisits, tick)
    .filter(a => !nextApt || a.id !== nextApt.id);

  // Selected-day summary — the ONE stat surface (zone ③), derived from the same
  // polled list the blocks render, so the numbers can never disagree with the rows
  // below them. Rendered as framework KpiCards; the value keeps its status color.
  const counts = dayCounts(dayApts);
  const daySummary = [
    { label: t('dashboard.counts.total'),     value: counts.total,     accent: C.navy },
    { label: t('dashboard.counts.here'),      value: counts.here,      accent: statusColors.arrived.text },
    { label: t('dashboard.counts.scheduled'), value: counts.scheduled, accent: statusColors.scheduled.text },
    { label: t('dashboard.counts.completed'), value: counts.completed, accent: statusColors.completed.text },
    { label: t('dashboard.counts.cancelled'), value: counts.cancelled, accent: statusColors.cancelled.text },
  ];

  // Route «پایان ویزیت» through a confirm — closing a visit is not undoable, and the
  // پذیرش screen used to fire it on a single click.
  const [pendingEnd, setPendingEnd] = React.useState(null);
  const askEnd = (apt) => setPendingEnd(apt);

  // The date navigator lives in the PageHeader action cluster: prev/next day arrows,
  // the Jalali picker, and a jump-to-today shortcut when off today.
  const dateNav = (
    <>
      {/* «پذیرش در …» — the second context widget, beside the date. Which day and
          which clinic are the same kind of question: what am I looking at. */}
      <ClinicContextChip
        clinics={clinic.clinics}
        activeId={clinic.activeId}
        isAll={clinic.isAll}
        activeClinic={clinic.activeClinic}
        single={clinic.single}
        mode={clinic.mode}
        onSelect={clinic.setActive}
        counts={clinicCounts}
      />
      <NavBtn onClick={() => setSelISO(shiftISO(selISO, -1))}><ChevronRight size={18} style={MIRROR_ICON} /></NavBtn>
      <div style={{ width: 210 }}>
        <JalaliDatePicker
          value={selISO}
          onChange={v => setSelISO(v || todayISO)}
          placeholder={t('dashboard.date.pick')}
          height={40}
          fontSize={13}
        />
      </div>
      <NavBtn onClick={() => setSelISO(shiftISO(selISO, 1))}><ChevronLeft size={18} style={MIRROR_ICON} /></NavBtn>
      {!isToday && (
        // h-10 aligns the pill to the 40px date picker beside it; the tonal look
        // is now the framework `subtle` variant, not hand-rolled navy alphas.
        <Button variant="subtle" size="sm" className="h-10 whitespace-nowrap" onClick={() => setSelISO(todayISO)}>
          {t('dashboard.date.today')}
        </Button>
      )}
      {/* Only where the doctor can book somewhere: a button that always answers
          «you cannot book here» is furniture. */}
      {booking.canBook && (
        <Button size="sm" className="h-10 whitespace-nowrap gap-1.5" data-testid="reception-book" onClick={booking.openBook}>
          <Plus size={16} aria-hidden /> {booking.s('dashboard.book.open')}
        </Button>
      )}
    </>
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg, overflow: 'hidden' }}>

      {/* ① The shell owns brand + identity; the page opens with a PageHeader. The
          date navigator is the header's context widget (Archetype A cockpit). */}
      <PageHeader
        title={t('dashboard.title')}
        sub={dateLabel ? t('dashboard.subtitle', { date: dateLabel }) : undefined}
        autoFocus={arrivedFromClosure}
      >
        {dateNav}
      </PageHeader>

      {/* ── Scrollable body ───────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px' }}>

        {/* Error replaces the content region, not the whole page — the header stays
            visible so the date navigator remains operable. */}
        {error ? (
          <ErrorState
            error={error}
            title={t('errors.load.data')}
            onRetry={retry}
          />
        ) : (
          <>
            {/* ② Forgotten visits follow the doctor to every date — being day-bounded
                is how they got forgotten. */}
            {forgotten.length > 0 && (
              <ForgottenVisitBanner
                visits={forgotten}
                nowMs={tick}
                onContinue={apt => navigate('visit', { id: apt.patientId })}
                onEnd={askEnd}
              />
            )}

            {/* ③ KPI strip — the selected day's counts, one card each. */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
              {daySummary.map((s, i) => (
                <KpiCard key={i} label={s.label} value={formatDigits(s.value)} accent={s.accent} loading={loading} />
              ))}
            </div>

            {/* A filter that silently drops appointments is a clinical hazard, so
                the lens says what it is holding back and offers one click out of
                it. Absent when nothing is hidden — a permanent banner explaining
                that nothing is wrong is noise. */}
            {hiddenByClinic > 0 && (
              <Button
                variant="subtle"
                data-testid="reception-clinic-hidden"
                className="w-full justify-start gap-2 mb-4 h-10"
                onClick={() => clinic.setActive(ALL_CLINICS)}
              >
                <Building2 size={15} aria-hidden />
                <span style={{ flex: 1, textAlign: 'start', fontWeight: 500 }}>
                  {t('dashboard.clinic.hidden', { count: formatNumber(hiddenByClinic) })}
                </span>
                <span>{t('dashboard.clinic.showAll')}</span>
              </Button>
            )}

            {/* ⑤ P2 — main day sheet (wide, right) + rail (280px, left). */}
            <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'minmax(0, 1fr) 280px', gap: 20, alignItems: 'start' }}>

              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                {/* Next patient hero (today only) */}
                {!loading && isToday && nextApt && (
                  <NextPatientHero
                    apt={nextApt}
                    nowMin={nowMin}
                    nowMs={tick}
                    onStart={() => navigate('visit', { id: nextApt.patientId })}
                    onHistory={() => navigate('patient-detail', { id: nextApt.patientId })}
                    onEnd={() => askEnd(nextApt)}
                  />
                )}

                {/* The day sheet's own shape — a block strip over appointment rows —
                    while the header, KPIs and rail stay real (DESIGN §"Loading & skeleton"). */}
                {loading && <ReceptionDaySheetSkeleton rows={4} />}

                {/* «هیچ مطبی ندارم» is not «امروز نوبتی ندارم». For a doctor with
                    no clinic the empty state would be true every day forever and
                    would never say why, so the connection guide takes the slot —
                    without the Card wrapper, since it brings its own surface. */}
                {!loading && dayApts.length === 0 && clinic.none && (
                  <NoClinicGuide pending={clinic.pending} onJoined={clinic.refresh} />
                )}

                {/* The clinic lookup failed: we cannot say «no clinic», and an empty
                    day may be a filtered one — so say what actually happened. */}
                {!loading && dayApts.length === 0 && clinic.error && (
                  <ErrorState error={clinic.error} title={t('errors.load.data')} onRetry={clinic.refresh} />
                )}

                {!loading && dayApts.length === 0 && !clinic.none && !clinic.error && (
                  <Card style={{ padding: 0, border: 'none', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <NoAppointments
                      icon={<CalendarDays size={52} color={C.textMuted} strokeWidth={1.5} />}
                      title={t('dashboard.empty.none')}
                      description={t('dashboard.empty.noneHint')}
                      doctorId={doctor?.id}
                      navigate={navigate}
                      onBook={booking.openBook}
                      canBook={booking.canBook}
                    />
                  </Card>
                )}

                {!loading && dayApts.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                    {/* صف اتاق پزشک — the patients who are actually here, in the order the
                        clinic desk set. Hidden when nobody is: a مطب with no reception
                        desk never marks anyone arrived, and an always-empty panel is just
                        furniture. */}
                    {queue.length > 0 && (
                      <Block title={t('dashboard.queue.room')} count={queue.length} countLabel={t('dashboard.queue.roomCount')} tint={statusColors.in_visit.bg} tintText={statusColors.in_visit.text}>
                        {queue.map(apt => (
                          <AppointmentRow
                            key={apt.id}
                            apt={apt}
                            queuePos={queuePosOf.get(apt.id)}
                            nowMs={tick}
                            clinicName={clinicNameOf(apt)}
                            onAction={() => navigate('visit', { id: apt.patientId })}
                          />
                        ))}
                      </Block>
                    )}

                    {upcomingList.length > 0 && (
                      <Block title={t('dashboard.queue.upcoming')} count={upcomingList.length} countLabel={t('dashboard.queue.upcomingCount')} tint={soft(C.navy, 0.06)} tintText={C.navy}>
                        {upcomingList.map(apt => (
                          <AppointmentRow
                            key={apt.id}
                            apt={apt}
                            nowMs={tick}
                            clinicName={clinicNameOf(apt)}
                            onAction={() => navigate('visit', { id: apt.patientId })}
                            // «ثبت حضور» is what makes the queue exist in a مطب with no
                            // desk — without it, a solo doctor can never build one. The label is
                            // imperative, not past tense: «حاضر شد» read as a claim that the
                            // نوبت‌دار had already arrived.
                            onArrived={isToday ? () => arrive(apt.id) : null}
                            arriving={arrivingIds.has(apt.id)}
                            onWithdraw={() => booking.askWithdraw(apt)}
                            onReschedule={booking.canReschedule(apt) ? () => booking.askReschedule(apt) : null}
                          />
                        ))}
                      </Block>
                    )}

                    {/* Finished work is the biggest block by evening and the least useful:
                        it starts collapsed, unless it is all there is. */}
                    {done.length > 0 && (
                      <Block
                        title={t('dashboard.queue.finished')} count={done.length} countLabel={t('dashboard.queue.finishedCount')}
                        tint={CT.surface2} tintText={C.textSecondary}
                        collapsible defaultOpen={queue.length === 0 && upcomingList.length === 0}
                      >
                        {done.map(apt => (
                          <AppointmentRow
                            key={apt.id}
                            apt={apt}
                            nowMs={tick}
                            clinicName={clinicNameOf(apt)}
                            onAction={() => navigate('visit', { id: apt.patientId })}
                          />
                        ))}
                      </Block>
                    )}
                  </div>
                )}
              </div>

              {/* ── Rail: money and the week, quiet unless something is wrong ──── */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <IncomeCard income={income} currency={displayCurrencyFor(clinic.clinics, clinic.activeClinic?.id)} onOpen={() => navigate('accounting')} />
                <CreditsCard balance={walletBalance} onOpen={() => navigate('credits')} />
                <WeekStrip days={weekDays} selISO={selISO} onPick={setSelISO} />
              </div>
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={!!pendingEnd}
        onOpenChange={(o) => { if (!o) setPendingEnd(null); }}
        title={t('dashboard.endVisitConfirm.title')}
        message={t('dashboard.endVisitConfirm.message', {
          name: pendingEnd?.name || t('common.labels.patient'),
        })}
        confirmLabel={t('dashboard.endVisitConfirm.confirm')}
        cancelLabel={t('dashboard.endVisitConfirm.cancel')}
        destructive
        // Returned, not fired-and-forgotten: the confirm stays open until the
        // write settles and reports a failure inline.
        onConfirm={() => (pendingEnd ? endVisit(pendingEnd.id) : undefined)}
      />

      <BookAppointmentDialog
        open={booking.bookOpen}
        onClose={booking.closeBook}
        dayISO={selISO}
        clinicId={clinic.isAll ? null : clinic.activeId}
        bookingClinics={booking.bookingClinics}
        onBooked={booking.onBooked}
        dayAppointments={allAppointments}
        isMobile={false}
      />

      <RescheduleAppointmentDialog
        open={!!booking.rescheduling}
        apt={booking.rescheduling}
        onClose={booking.closeReschedule}
        onRescheduled={booking.onRescheduled}
        isMobile={false}
      />

      <ConfirmDialog
        open={!!booking.pendingWithdraw}
        onOpenChange={(o) => { if (!o) booking.cancelWithdraw(); }}
        title={booking.s('dashboard.withdraw.title')}
        message={booking.s('dashboard.withdraw.message', {
          name: booking.pendingWithdraw?.name || t('common.labels.patient'),
        })}
        confirmLabel={booking.s('dashboard.withdraw.confirm')}
        cancelLabel={booking.s('dashboard.withdraw.keep')}
        destructive
        onConfirm={booking.confirmWithdraw}
      />
    </div>
  );
};

export default ReceptionWeb;
